import * as Blockly from 'blockly/core';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { BoardType } from '@/(routers)/boards/editor/block/blockConfig';
import { defineBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import { FieldOperatorDropdown } from '@/(routers)/boards/editor/block/definitions/fields/fieldOperatorDropdown';
import { FieldStyledBadgeLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledBadgeLabel';
import { FieldStyledLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledLabel';
import { ALCOHOL_TYPE_OPTIONS } from '@/(routers)/boards/editor/block/generators/arduino/blocks/sensing/helper/analog_helper';

type TypeValue = 'RAW' | 'BREATH' | 'BLOOD';

const BADGE_MAP: Record<TypeValue, string> = {
    RAW: '',
    BREATH: 'mg/L',
    BLOOD: 'mg/L',
};

const POSITION_OPTIONS = {
    orange: {
        rectColour: CATEGORIES.sensing.color,
        options: [
            { value: 'UPPER_LEFT', label: '左上' },
            { value: 'UPPER_RIGHT', label: '右上' },
        ],
    },
    blue: {
        rectColour: CATEGORIES.sensing.color,
        options: [
            { value: 'UPPER_LEFT', label: '左上' },
            { value: 'UPPER_RIGHT', label: '右上' },
            { value: 'LOWER_LEFT', label: '左下' },
            { value: 'LOWER_RIGHT', label: '右下' },
        ],
    },
} as const;

const syncFields = (block: Blockly.Block, board: BoardType) => {
    const posField = block.getField('POSITION') as FieldOperatorDropdown | null;
    if (posField) posField.setOptions([...POSITION_OPTIONS[board].options]);

    const typeField = block.getField('TYPE') as FieldOperatorDropdown | null;
    if (!typeField) return;
    if (board === 'blue') {
        typeField.setOptions([{ value: 'RAW', label: 'センサー値' }]);
        const badge = block.getField('BADGE') as FieldStyledBadgeLabel | null;
        if (badge) badge.setValue('');
    } else {
        typeField.setOptions([...ALCOHOL_TYPE_OPTIONS]);
    }
};

defineBlock({
    type: 'alcohol',
    colour: CATEGORIES.sensing.color,
    textColour: '#FFFFFF',
    tooltip: 'アルコールセンサーの値を読み取ります',
    movable: true,
    deletable: true,
    output: 'Number',
    inputs: [
        {
            kind: 'dummy',
            fields: [
                { type: 'image', icon: { src: '/images/block/alcohol.svg', size: 65 } },
                {
                    type: 'field',
                    instance: () => new FieldStyledLabel('アルコールセンサー', { fontSize: 15, fontWeight: 'bold' }),
                },
                {
                    type: 'field',
                    name: 'BADGE',
                    instance: () => new FieldStyledBadgeLabel(''),
                },
                { type: 'label', text: ' ' },
                {
                    type: 'dropdown',
                    name: 'POSITION',
                    rectColour: POSITION_OPTIONS.orange.rectColour,
                    options: [...POSITION_OPTIONS.orange.options],
                },
                { type: 'label', text: 'で' },
                {
                    type: 'dropdown',
                    name: 'TYPE',
                    rectColour: POSITION_OPTIONS.orange.rectColour,
                    options: [...ALCOHOL_TYPE_OPTIONS],
                },
                { type: 'label', text: 'を計測する ' },
            ],
        },
    ],
    onInit: function (this: Blockly.Block) {
        syncFields(this, useBoardEditorStore.getState().board);
    },
    onBoardChange: (block, board) => {
        syncFields(block, board);
    },
    onFieldChange: (block, fieldName, newValue) => {
        if (fieldName !== 'TYPE') return;
        const badge = block.getField('BADGE') as FieldStyledBadgeLabel | null;
        if (!badge) return;
        badge.setValue(BADGE_MAP[newValue as TypeValue] ?? '');
    },
});
